import React from 'react';
import { Package, TrendingUp, IndianRupee, BarChart3, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../config';
import { SummaryCard, MetricCard } from './SharedCards';
import { deltaForKey } from './trendUtils';

const COST_BREAKDOWN_ITEMS = [
  ['Power', 'power_cost_per_mt', 'power'],
  ['Steam', 'steam_cost_per_mt', 'steam'],
  ['Water', 'water_cost_per_mt', 'water'],
  ['Oil', 'oil_cost_per_mt', 'oil'],
  ['Carbon', 'carbon_cost_per_mt', 'carbon'],
  ['Alumina', 'alumina_cost_per_mt', 'alumina'],
  ['13X Mol Sieves', 'mol_sieves_cost_per_mt', 'mol_sieves'],
  ['NH3', 'nh3_cost_per_mt', 'nh3'],
  ['KMNO4', 'kmno4_cost_per_mt', 'kmno4'],
  ['Maintenance', 'maintenance_cost_per_mt', 'maintenance'],
  ['Food Grade', 'food_grade_cost_per_mt', 'food_grade'],
];

const DailyDataView = ({ dailyData, trendData, stoppageMins = 0, onStoppageClick, openSummaryDrillDown, openDrillDown }) => (
  <div className="space-y-6">
    {dailyData.is_consolidated && (
      <div className="bg-[#E8F2FC] border border-[#156ACA]/20 rounded-lg p-4">
        <h4 className="font-semibold text-[#156ACA]">Consolidated View - {dailyData.plants_included?.length || 0} Plants</h4>
        <p className="text-sm text-[#156ACA] mt-1">
          Includes: {dailyData.plants_included?.join(', ') || 'No plants'}
        </p>
      </div>
    )}

    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
      <SummaryCard title="Production" value={`${dailyData.total_production_mt} MT`} color="blue" icon={<Package />} summaryKey="production" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'production')} />
      <SummaryCard title="Despatches" value={`${dailyData.total_despatches_mt} MT`} color="green" icon={<TrendingUp />} summaryKey="despatches" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'despatches')} />
      <SummaryCard title="Total Cost" value={formatCurrency(dailyData.total_cost)} color="orange" icon={<IndianRupee />} summaryKey="cost" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'cost')} deltaInvert />
      <SummaryCard title="Cost/MT" value={formatCurrency(dailyData.cost_efficiency?.total_cost_per_mt || dailyData.cost_per_mt || 0)} color="purple" icon={<BarChart3 />} summaryKey="cost_per_mt" onClick={openSummaryDrillDown} delta={deltaForKey(trendData, 'cost_per_mt')} deltaInvert />
      <SummaryCard title="Stoppage" value={`${Number(stoppageMins || 0).toLocaleString('en-IN')} min`} color="orange" icon={<AlertTriangle />} summaryKey="stoppage" onClick={onStoppageClick} />
    </div>

    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Volume Efficiency (Consumption per MT)</h3>
        <span className="text-xs text-[#156ACA]">Tap any metric for monthly trend</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          label="Power/MT"
          value={`${dailyData.volume_efficiency?.power_units_per_mt || 0} U`}
          subValue={`${dailyData.volume_efficiency?.power_kgs_per_unit || 0}`}
          subLabel="kgs/Unit"
          metricKey="power"
          onClick={openDrillDown}
        />
        <MetricCard label="Steam/MT" value={`${dailyData.volume_efficiency?.steam_mt_per_mt || 0} MT`} metricKey="steam" onClick={openDrillDown} />
        <MetricCard label="Water/MT" value={`${dailyData.volume_efficiency?.water_kl_per_mt || 0} KL`} metricKey="water" onClick={openDrillDown} />
        <MetricCard label="Oil/MT" value={`${dailyData.volume_efficiency?.oil_l_per_mt || 0} L`} metricKey="oil" onClick={openDrillDown} />
        <MetricCard label="Carbon/MT" value={`${dailyData.volume_efficiency?.carbon_kgs_per_mt || 0} Kg`} metricKey="carbon" onClick={openDrillDown} />
        <MetricCard label="Alumina/MT" value={`${dailyData.volume_efficiency?.alumina_kgs_per_mt || 0} Kg`} metricKey="alumina" onClick={openDrillDown} />
        <MetricCard label="13X Mol Sieves/MT" value={`${dailyData.volume_efficiency?.mol_sieves_kgs_per_mt || 0} Kg`} metricKey="mol_sieves" onClick={openDrillDown} />
        <MetricCard label="NH3/MT" value={`${dailyData.volume_efficiency?.nh3_kgs_per_mt || 0} Kg`} metricKey="nh3" onClick={openDrillDown} />
        <MetricCard label="KMNO4/MT" value={`${dailyData.volume_efficiency?.kmno4_kgs_per_mt || 0} Kg`} metricKey="kmno4" onClick={openDrillDown} />
      </div>
    </div>

    {dailyData.cost_breakdown && (dailyData.cost_breakdown.co2_purchase || 0) > 0 && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-testid="daily-co2-purchase-panel">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">CO₂ Purchase Cost{dailyData.is_consolidated ? ' (Consolidated)' : ''}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard label="Base CO₂ Cost" value={formatCurrency(dailyData.cost_breakdown.co2_purchase_base || 0)} metricKey="co2_base" />
          <MetricCard label="Loss Adjustment" value={formatCurrency(dailyData.cost_breakdown.co2_loss_extra || 0)} metricKey="co2_loss_extra" />
          <div className="bg-[#EEF2FF] p-3 rounded-lg border border-[#156ACA]/30">
            <p className="text-xs text-[#156ACA] font-semibold">CO₂ Cost (Loss-Adjusted) - used in Total</p>
            <p className="text-xl font-bold text-[#156ACA]">{formatCurrency(dailyData.cost_breakdown.co2_purchase || 0)}</p>
          </div>
        </div>
      </div>
    )}

    {dailyData.cost_efficiency && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Cost Breakdown (Rs/MT){dailyData.is_consolidated ? ' (Consolidated)' : ''}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
          {COST_BREAKDOWN_ITEMS.map(([label, key, mk]) => (
            <MetricCard key={mk} label={label} value={formatCurrency(dailyData.cost_efficiency?.[key] || 0)} />
          ))}
        </div>
      </div>
    )}

    {!dailyData.is_consolidated && dailyData.shifts_data?.length > 0 && (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Shift-wise Data</h3>
        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Production (Kg)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Despatches (Kg)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Power</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Steam (MT)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dailyData.shifts_data.map(shift => (
                <tr key={shift.shift}>
                  <td className="px-4 py-3 font-medium">Shift {shift.shift}</td>
                  <td className="px-4 py-3 text-right">{shift.production_kgs}</td>
                  <td className="px-4 py-3 text-right">{shift.despatches_kgs}</td>
                  <td className="px-4 py-3 text-right">{shift.power_consumption_units}</td>
                  <td className="px-4 py-3 text-right">{shift.steam_consumption_mt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

export default DailyDataView;
